import { InfluencersProfileFormat, ProposalDownloadFormat } from '@anytag/graphql';
import { ButtonDropdown, DownloadSubmenuIcon } from '@anytag/shared-atoms';
import { useDownloadFile, useQueryHelper, useToggle } from '@anytag/shared-hooks';
import { DownloadFileType, DropdownOption, MenuTypeLevel } from '@anytag/shared-types';
import { cn, getRestApiUrl } from '@anytag/shared-utils';
import { useUserRoles } from '@anytag/auth';
import { GoogleAuthDialog, useGoogleAuthDownload } from '@anytag/shared-molecules';
import { usePackageDownloads } from './usePackageDownloads';

interface PackageInfluencersDownloadButtonProps {
  disabled?: boolean;
  className?: string;
  packageId: number | null;
  selectedInfluencerIds?: number[];
}

export const PackageInfluencersDownloadButton = ({
  disabled,
  className,
  packageId,
  selectedInfluencerIds = []
}: PackageInfluencersDownloadButtonProps) => {
  const { isAdvertiser } = useUserRoles();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { on: downloading, setOn, setOff } = useToggle();
  const { callRequestPackageProposalPptx, callRequestPackageProposalExcel } = usePackageDownloads();
  const { handleDownloadFile, downloading: csvDownloading } = useDownloadFile();
  const { handlePackageProposalGoogleSlideDownload, dialogProps, handlePackageProposalGoogleSpreadsheetDownload } =
    useGoogleAuthDownload({
      goBackUrl: '/package/$id/content/list',
      influencerIds: selectedInfluencerIds,
      packageId
    });

  const downloadOptions: DropdownOption[] = [
    {
      value: 'Profile Sheet',
      menuType: MenuTypeLevel.MULTI_LEVEL,
      label: t<string>('Profile Sheet'),
      submenu: [
        {
          label: t<string>('PowerPoint'),
          value: DownloadFileType.PPTX,
          prefixIcon: <DownloadSubmenuIcon icon="powerPoint" />
        },
        {
          label: t<string>('Google Slide'),
          value: DownloadFileType.GOOGLE_SLIDE,
          prefixIcon: <DownloadSubmenuIcon icon="googleSlide" />
        }
      ]
    },
    {
      value: 'Proposal List',
      menuType: MenuTypeLevel.MULTI_LEVEL,
      label: t('Proposal List'),
      submenu: [
        {
          label: t<string>('CSV'),
          value: DownloadFileType.CSV,
          prefixIcon: <DownloadSubmenuIcon icon="csv" />
        },
        ...(!isAdvertiser
          ? [
              {
                label: t<string>('Excel'),
                value: DownloadFileType.EXCEL,
                prefixIcon: <DownloadSubmenuIcon icon="excel" />
              },
              {
                label: t<string>('Spreadsheet'),
                value: DownloadFileType.SPREADSHEET,
                prefixIcon: <DownloadSubmenuIcon icon="spreadsheet" />
              }
            ]
          : [])
      ]
    }
  ];

  const handleDownloadOptionSelected = async (option: DropdownOption) => {
    setOn();

    if (packageId) {
      try {
        switch (option.value) {
          case DownloadFileType.CSV:
            await handleDownloadFile({
              url: getRestApiUrl(`package/${packageId}/influencers/csv/`),
              filenamePrefix: 'package_influencers',
              params: { influencerIds: selectedInfluencerIds }
            });
            break;
          case DownloadFileType.PPTX:
            await callRequestPackageProposalPptx({
              variables: {
                packageId,
                format: InfluencersProfileFormat.PPTX,
                influencerIds: selectedInfluencerIds
              }
            });
            enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
            break;
          case DownloadFileType.EXCEL:
            await callRequestPackageProposalExcel({
              variables: { packageId, influencerIds: selectedInfluencerIds, format: ProposalDownloadFormat.EXCEL }
            });
            enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
            break;
          // Google Auth required for Slide and Spreadsheet
          case DownloadFileType.SPREADSHEET:
            await handlePackageProposalGoogleSpreadsheetDownload();
            break;
          case DownloadFileType.GOOGLE_SLIDE:
            await handlePackageProposalGoogleSlideDownload();
            break;

          default:
            break;
        }
      } catch (error) {
        enqueueSnackbar(t('failedToDownload'), { variant: 'error' });
      } finally {
        setOff();
      }
    }
  };

  return (
    <>
      <ButtonDropdown
        variant="outline"
        align="end"
        side="bottom"
        disabled={disabled}
        loading={downloading || csvDownloading}
        className={cn('min-w-33 uppercase', className)}
        options={downloadOptions}
        title={t('Button.Download')}
        onSelectOption={handleDownloadOptionSelected}
        prefixIcon={{ icon: 'download', size: '14px' }}
      />
      <GoogleAuthDialog {...dialogProps} />
    </>
  );
};
